import { IObject, ObjectTypes, TargetImageData } from "utils/interface.utils";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { createObjectButton, updateObjectButton } from "components/editor_workspace/scene/object_button.helper";
import { createObjectImage, updateObjectImage } from "components/editor_workspace/scene/object_image.helper";
import { createObjectSocial, updateObjectSocial } from "components/editor_workspace/scene/object_social.helper";
import { createObjectText, updateObjectText } from "components/editor_workspace/scene/object_text.helper";
import { createObjectVideo, updateObjectVideo } from "components/editor_workspace/scene/object_video.helper";
import { cleanupObjects } from "components/editor_workspace/scene/workspace_scene.helper";

export const createAndUpdateObjectsOfTargetImage = (workspace: WorkspaceContext, targetImageData: TargetImageData) => {
    const { rendererRef, sceneRef, cameraRef, targetImageRef, objectsRef, targetImageDataListRef } = workspace.refs;
    const { targetImageDataList } = workspace;
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;

    // If no target image data, clean up all objects
    if (!targetImageData) {
        cleanupObjects(workspace);
        return;
    }

    const targetImageObjects = targetImageData.objects;

    if (!targetImageObjects || targetImageObjects.length === 0) {
        // Proper cleanup of all objects
        cleanupObjects(workspace);
        return;
    }

    if (targetImageRef.current && !sceneRef.current.children.includes(targetImageRef.current)) {
        sceneRef.current.add(targetImageRef.current);
    }

    // Track which objects are still present in the updated data
    const currentObjectIDs = new Set<string>();

    // Process each object in the data
    targetImageObjects.forEach((object: IObject) => {
        const objectID = object.id;
        currentObjectIDs.add(objectID);

        // Create or update the object based on its type
        switch (object.type) {
            case ObjectTypes.IMAGE:
                if (objectsRef.current.has(objectID)) {
                    updateObjectImage(workspace, objectID, object.properties);
                } else if (object.properties.src) {
                    createObjectImage(workspace, objectID, object.properties);
                }
                break;
            case ObjectTypes.VIDEO:
                if (objectsRef.current.has(objectID)) {
                    updateObjectVideo(workspace, objectID, object.properties);
                } else if (object.properties.src) {
                    createObjectVideo(workspace, objectID, object.properties);
                }
                break;
            case ObjectTypes.BUTTON:
                if (objectsRef.current.has(objectID)) {
                    updateObjectButton(workspace, objectID, object.properties);
                } else {
                    createObjectButton(workspace, objectID, object.properties);
                }
                break;
            case ObjectTypes.TEXT:
                if (objectsRef.current.has(objectID)) {
                    updateObjectText(workspace, objectID, object.properties);
                } else {
                    createObjectText(workspace, objectID, object.properties);
                }
                break;
            case ObjectTypes.SOCIAL:
                if (objectsRef.current.has(objectID)) {
                    updateObjectSocial(workspace, objectID, object.properties);
                } else {
                    createObjectSocial(workspace, objectID, object.properties);
                }
                break;
            default:
                console.warn(`Unknown object type: ${object.type}`);
        }
    });

    targetImageDataListRef.current = targetImageDataList;

    // Remove any objects that are no longer in the data
    const objectsToRemove: string[] = [];

    objectsRef.current.forEach((objectData, objectId) => {
        if (!currentObjectIDs.has(objectId)) {
            objectsToRemove.push(objectId);
        }
    });

    // Remove objects in a separate loop to avoid modifying while iterating
    objectsToRemove.forEach(objectId => {
        const objectData = objectsRef.current.get(objectId);
        if (objectData) {
            if (objectData.cleanup) {
                objectData.cleanup();
            } else if (objectData.mesh && sceneRef.current) {
                sceneRef.current.remove(objectData.mesh);
            }
            objectsRef.current.delete(objectId);
        }
    });

    // Force a render update
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
}
